import React from 'react';
import styles from './documents-head.module.scss';
import UTESearchBox from 'src/components/ute-search-box';
import SearchIcon from 'src/assets/svg/search-icon';
import UTEButton from 'src/components/ute-button';
import FilterIcon from 'src/assets/svg/filter-icon';
import {
	DocumentsDisplay,
	DocumentsSort,
	ExchangeFileType,
	FileSystemType,
	IFileDetails,
	IFilter,
	IMyDownloadRequestModel,
	ITaxDocument,
	IPaginatedNode,
	ISingleDownloadRequest,
	ITaxRecord,
	ITaxReturnDetails,
} from '../documents-types';
import DocumentsSortComponent from '../documents-sort';
import { generateExchangeTaxpayerAPIURL } from 'src/modules/common/common-api';
import { SendFilesIcon } from 'src/assets/svg/send-files-icon';
import UTEClientViewWrapper from 'src/components/ute-client-view-wrapper';
import { getNumberOfFilesAndFoldersCount, getTaxYearFromName, Guid } from 'src/helper/utils';
import { DownloadIcon } from 'src/assets/svg/download-icon';
import { SSE_DOCUMENT_CONSTANTS, SSR_DOCUMENTS_PREVIEW_CONSTANTS, TAX_RETURN_FOLDER_NAME } from 'src/helper/constants';
import { UTENotifier } from 'src/modules/notification/ute-notifier';
import { DisplayDownloadFile } from 'src/helper/display-download-file';
import DownloadSuccessToaster from '../documents-download-toaster';
import { AxiosResponse } from 'axios';
import { getExchangeSasUrl, requestDownloadSelectedData, requestSingleFileRequest } from '../documents-api';
import { ProductType } from 'src/modules/common/common-types';
import { DocumentType } from 'src/modules/documents/documents-types';
import { DeviceType, useDeviceType } from 'src/hooks/useDeviceType';

type DocumentsHeadProps = {
	searchText: string;
	showClearButton: boolean;
	onChangeSearchText: (searchText: string) => void;
	onSearch: () => void;
	onClearSearch: () => void;
	onClickFilter: () => void;
	sortOrder: DocumentsSort;
	onSort: (sortOrderReceived: DocumentsSort) => void;
	onClearSort: () => void;
	filter: IFilter;
	showSendFiles: boolean;
	records: ITaxRecord[];
	paginatedRecords: IPaginatedNode[];
	display: DocumentsDisplay;
	clearAllSelectionsInTree: (records: ITaxRecord[]) => void;
	clearAllSelectionsInGrid: (paginatedRecords: IPaginatedNode[]) => void;
	isSomethingChecked: () => boolean;
	isSearchDisabled: boolean;
};
const DocumentsHead = ({
	searchText,
	showClearButton,
	onChangeSearchText,
	onSearch,
	onClearSearch,
	onClickFilter,
	filter,
	sortOrder,
	onSort,
	onClearSort,
	showSendFiles,
	records,
	paginatedRecords,
	display,
	clearAllSelectionsInTree,
	clearAllSelectionsInGrid,
	isSomethingChecked,
	isSearchDisabled,
}: DocumentsHeadProps) => {
	const deviceType = useDeviceType();

	const showText = () => {
		if (deviceType !== DeviceType.Desktop) {
			return '';
		} else {
			const { fromDate, toDate, filterStatus, filterFileType } = filter;
			let count = 0;
			const filterStatusArray = filterStatus.length > 0 ? filterStatus.split(',') : [];
			const filterFileTypeArray = filterFileType.length > 0 ? filterFileType.split(',') : [];
			if (fromDate && toDate) count++;
			count += filterStatusArray.length + filterFileTypeArray.length;
			if (count === 0) {
				return 'Filter';
			} else {
				return (
					<span>
						Filter&nbsp;/&nbsp;<span className={styles.count}>{count}</span>
					</span>
				);
			}
		}
	};

	const prepareMyDownloadRequest = (node: ITaxRecord, myDownloadRequestModel: IMyDownloadRequestModel[]) => {
		const tempFiles: IFileDetails[] = [];

		if (node.name.indexOf(TAX_RETURN_FOLDER_NAME) === 0 && getNumberOfFilesAndFoldersCount(node) == 0) {
			const taxYear = getTaxYearFromName(node.name);
			myDownloadRequestModel.push({ taxYear: taxYear, files: [], rootFolderName: node.name });
		} else if (node.type === FileSystemType.Folder) {
			let taxYear: number = 0;
			const traverse = (eachNode: ITaxRecord) => {
				if (eachNode.type === FileSystemType.File) {
					const documents = eachNode.documents;
					taxYear = documents.taxYear;
					if (documents.productType == ProductType.Gather) {
						tempFiles.push({
							documentId: documents.documentId,
							documentName: eachNode.name,
							documentGuid:
								documents.documentGuid === Guid.empty ? documents.fileGuid ?? Guid.empty : documents.documentGuid,
							productType: documents.productType,
							signerType: documents.signerType,
							signerName: documents.signerName,
							documentType: documents.documentType,
							fileGuid: documents.fileGuid ?? Guid.empty,
							gatherId: documents.gatherId,
							clientId: documents.clientId,
							sourceDocumentId: documents.sourceDocumentId,
							signedOn: documents.signedOn,
							fileType: documents.fileType,
							sourceDocumentFileName: documents.sourceDocumentFileName,
							efileFormName: eachNode.name,
						});
					} else {
						tempFiles.push({
							documentId: documents.documentId,
							documentName: eachNode.name,
							documentGuid: documents.documentGuid,
							productType: documents.productType,
							signerType: documents.signerType,
							signerName: documents.signerName,
							documentType: documents.documentType,
							clientId: documents.clientId,
							fileGuid: documents.fileGuid ?? Guid.empty,
							sourceDocumentFileName: documents.sourceDocumentFileName,
							signedOn: documents.signedOn,
							fileType: documents.fileType,
							efileFormName: eachNode.name,
						});
					}
				}
				eachNode.children.forEach(traverse);
			};
			traverse(node);
			myDownloadRequestModel.push({
				files: tempFiles,
				rootFolderName: node.name,
				taxYear: taxYear,
			});
		} else {
			const documents = node.documents;
			if (documents.productType == ProductType.Gather) {
				tempFiles.push({
					documentId: documents.documentId,
					documentName: node.name,
					documentGuid:
						documents.documentGuid === Guid.empty ? documents.fileGuid ?? Guid.empty : documents.documentGuid,
					productType: documents.productType,
					signerType: documents.signerType,
					signerName: documents.signerName,
					documentType: documents.documentType,
					fileGuid: documents.fileGuid ?? Guid.empty,
					gatherId: documents.gatherId,
					clientId: documents.clientId,
					sourceDocumentId: documents.sourceDocumentId,
					signedOn: documents.signedOn,
					fileType: documents.fileType,
					sourceDocumentFileName: documents.sourceDocumentFileName,
					efileFormName: node.name,
				});
			} else {
				tempFiles.push({
					documentId: documents.documentId,
					documentGuid: documents.documentGuid,
					documentName: node.name,
					productType: documents.productType,
					signerType: documents.signerType,
					signerName: documents.signerName,
					documentType: documents.documentType,
					clientId: documents.clientId,
					fileGuid: documents.fileGuid ?? Guid.empty,
					sourceDocumentFileName: documents.sourceDocumentFileName,
					signedOn: documents.signedOn,
					fileType: documents.fileType,
					efileFormName: node.name,
				});
			}
			myDownloadRequestModel.push({ taxYear: documents.taxYear, files: tempFiles, rootFolderName: '' });
		}
	};

	const generateSelectedData = () => {
		const myDownloadRequestModel: IMyDownloadRequestModel[] = [];

		if (display === DocumentsDisplay.TREE) {
			records.forEach((eachNode: ITaxRecord) => {
				const traverse = (currentNode: ITaxRecord) => {
					let found = false;
					if (!found) {
						if (currentNode.isChecked) {
							prepareMyDownloadRequest(currentNode, myDownloadRequestModel);
							found = true;
						}
					}
					!found && currentNode.children.forEach(traverse);
				};
				traverse(eachNode);
			});
		} else if (display === DocumentsDisplay.GRID) {
			paginatedRecords
				.filter((x) => x.isChecked)
				.forEach((eachRecord: IPaginatedNode) => {
					const taxYear = getTaxYearFromName(eachRecord.path.split(',')[0]);
					//eachrecord is a folder
					if ('folderName' in eachRecord) {
						myDownloadRequestModel.push({ files: [], taxYear: taxYear, rootFolderName: eachRecord.folderName });
					} else {
						myDownloadRequestModel.push({
							files: [
								{
									documentId: eachRecord.documentId,
									documentGuid: eachRecord.documentGuid,
									documentName: eachRecord.efileFormName,
									productType: eachRecord.productType,
									signerType: eachRecord.signerType,
									signerName: eachRecord.signerName,
									documentType: eachRecord.documentType,
									clientId: eachRecord.clientId,
									fileGuid: eachRecord.fileGuid ?? Guid.empty,
									sourceDocumentFileName: eachRecord.sourceDocumentFileName,
									efileFormName: eachRecord.efileFormName,
									fileType: eachRecord.fileType,
									signedOn: eachRecord.signedOn,
								},
							],
							taxYear: taxYear,
							rootFolderName: '',
						});
					}
				});
		}

		if (
			myDownloadRequestModel.length === 1 &&
			myDownloadRequestModel[0].files.length === 1 &&
			!myDownloadRequestModel[0].rootFolderName
		) {
			let singleDownloadRequest: ISingleDownloadRequest | ITaxDocument = {
				documentName: '',
				documentGuid: '',
				signerName: '',
				taxYear: 0,
				productType: ProductType.Returns,
				signedOn: '',
				fileType: ExchangeFileType.None,
				efileFormName: '',
			};
			if (myDownloadRequestModel[0].files[0].productType === ProductType.Organizers) {
				singleDownloadRequest = {
					documentType: DocumentType.None,
					documentId: 0,
					efileFormName: '',
					fileGuid: '',
					clientId: '',
				} as ITaxDocument;
				const singleFileDetails = myDownloadRequestModel[0].files[0];
				singleDownloadRequest.documentGuid = singleFileDetails.documentGuid;
				singleDownloadRequest.efileFormName = singleFileDetails.documentName;
				singleDownloadRequest.signerName = singleFileDetails.signerName ?? '';
				singleDownloadRequest.taxYear = myDownloadRequestModel[0].taxYear;
				singleDownloadRequest.productType = singleFileDetails.productType;
				singleDownloadRequest.documentType = singleFileDetails.documentType;
				singleDownloadRequest.documentId = singleFileDetails.documentId;
				singleDownloadRequest.fileGuid = singleFileDetails.fileGuid ?? Guid.empty;
				singleDownloadRequest.clientId = singleFileDetails.clientId ?? '';
				singleDownloadRequest.sourceDocumentFileName = singleFileDetails.sourceDocumentFileName;
			} else if (myDownloadRequestModel[0].files[0].productType === ProductType.Gather) {
				const singleFileDetails = myDownloadRequestModel[0].files[0];
				singleDownloadRequest.documentGuid = singleFileDetails.documentGuid;
				singleDownloadRequest.documentName = singleFileDetails.documentName;
				singleDownloadRequest.signerName = singleFileDetails.signerName ?? '';
				singleDownloadRequest.taxYear = myDownloadRequestModel[0].taxYear;
				singleDownloadRequest.productType = singleFileDetails.productType;
				singleDownloadRequest.documentType = singleFileDetails.documentType;
				singleDownloadRequest.sourceDocumentId = singleFileDetails.sourceDocumentId;
				singleDownloadRequest.sourceDocumentFileName = singleFileDetails.sourceDocumentFileName;
				singleDownloadRequest.gatherId = singleFileDetails.gatherId;
				singleDownloadRequest.clientId = singleFileDetails.clientId;
				singleDownloadRequest.signedOn = singleFileDetails.signedOn;
				singleDownloadRequest.fileType = singleFileDetails.fileType;
				singleDownloadRequest.efileFormName = singleFileDetails.efileFormName;
			} else {
				const singleFileDetails = myDownloadRequestModel[0].files[0];
				singleDownloadRequest.documentGuid = singleFileDetails.documentGuid;
				singleDownloadRequest.documentName = singleFileDetails.documentName;
				singleDownloadRequest.signerName = singleFileDetails.signerName;
				singleDownloadRequest.taxYear = myDownloadRequestModel[0].taxYear;
				singleDownloadRequest.productType = singleFileDetails.productType;
				singleDownloadRequest.signedOn = singleFileDetails.signedOn;
				singleDownloadRequest.fileType = singleFileDetails.fileType;
				singleDownloadRequest.efileFormName = singleFileDetails.efileFormName;
			}
			if (singleDownloadRequest.productType === ProductType.Exchange) {
				getExchangeSasUrl(singleDownloadRequest, singleFileRequestSuccessCallback, () => {
					UTENotifier.Error(SSE_DOCUMENT_CONSTANTS.ERROR_DOWNLOAD_DOCUMENT);
				});
			} else {
				requestSingleFileRequest(singleDownloadRequest, singleFileRequestSuccessCallback, () => {
					UTENotifier.Error(SSR_DOCUMENTS_PREVIEW_CONSTANTS.ERROR_DOWNLOAD_DOCUMENT);
				});
			}
		} else {
			requestDownloadSelectedData(
				myDownloadRequestModel,
				(response: AxiosResponse<boolean>) => {
					if (response) {
						UTENotifier.Success(<DownloadSuccessToaster />, false);
						clearAllSelectionsInTree(records);
						clearAllSelectionsInGrid(paginatedRecords);
					} else {
						UTENotifier.Error(SSR_DOCUMENTS_PREVIEW_CONSTANTS.ERROR_DOWNLOAD_DOCUMENT);
					}
				},
				() => {
					UTENotifier.Error(SSR_DOCUMENTS_PREVIEW_CONSTANTS.ERROR_DOWNLOAD_DOCUMENT);
				},
			);
		}
	};

	const singleFileRequestSuccessCallback = (response: AxiosResponse<string> | string) => {
		clearAllSelectionsInTree(records);
		clearAllSelectionsInGrid(paginatedRecords);
		const displayDownloadFile = new DisplayDownloadFile();
		if (!response && typeof response !== 'string' && !response.data) {
			UTENotifier.Success(<DownloadSuccessToaster />, false);
			return;
		}
		if (typeof response === 'string') {
			displayDownloadFile.directDownload(response);
		} else {
			displayDownloadFile.directDownload(response.data);
		}
	};

	return (
		<section className={styles.documentsHeadContainer}>
			<div className={styles.headActionsContainer}>
				<div className={styles.leftActions}>
					<UTESearchBox
						searchText={searchText}
						placeholderText='Search'
						searchIcon={<SearchIcon />}
						disabled={isSearchDisabled}
						searchOnClick={true}
						showClearButton={showClearButton}
						totalWidth={`${deviceType === DeviceType.Desktop ? '350px' : '234px'}`}
						height='38px'
						maxLength={260}
						onChangeSearchText={onChangeSearchText}
						onSearch={onSearch}
						onClearSearch={onClearSearch}
					/>
					<UTEButton
						text={showText()}
						icon={<FilterIcon />}
						className={styles.filter}
						onClick={onClickFilter}
						disabled={false}
						dataTestAuto='8c4abcc5-dd8f-420a-805e-c42d1697a0d7'
					/>
				</div>
				<div className={styles.rightActions}>
					<>
						{isSomethingChecked() ? (
							<UTEClientViewWrapper dataTestAuto='6794d491-d0dd-4c15-9abd-4787a4c78328'>
								<UTEButton
									icon={React.createElement(DownloadIcon, { className: styles.downloadIcon })}
									className={styles.btnDownload}
									dataTestAuto='898ac6e3-a0e5-4b82-be26-65e43208f29c'
									text='Download'
									onClick={generateSelectedData}
									disabled={false}
									title='Download'
								/>
							</UTEClientViewWrapper>
						) : (
							<>
								<DocumentsSortComponent
									sortOrder={sortOrder}
									onSort={onSort}
									onClearSort={onClearSort}
								/>
								{showSendFiles && (
									<UTEClientViewWrapper dataTestAuto='6a836002-9d92-4567-9913-4bb349bd311e'>
										<UTEButton
											icon={React.createElement(SendFilesIcon, { className: styles.sendFilesIcon })}
											className={styles.btnSendFiles}
											dataTestAuto='65c92414-e726-4f38-a333-023fca374731'
											text='Send Files'
											onClick={() => {
												generateExchangeTaxpayerAPIURL();
											}}
											disabled={false}
											title='Send files to the firm'
										/>
									</UTEClientViewWrapper>
								)}
							</>
						)}
					</>
				</div>
			</div>
		</section>
	);
};

export default DocumentsHead;
